import React, { useMemo } from 'react';
import { Tooltip, Badge } from 'evergreen-ui';
import { LessonDeadline } from '@shared/types';
import { ReactComponent as CalendarClockIcon } from '../../../../assets/icons/calendar-clock-regular.svg';

export const LessonDeadlineBadge = ({
    deadline,
    enrollmentDate,
}: {
    deadline?: LessonDeadline;
    enrollmentDate?: number;
}) => {
    const now = useMemo(() => new Date(), []);

    const isOverdue = useMemo((): boolean => {
        if (!deadline) return false;
        if (deadline.type === 'none') return false;
        if (deadline.type === 'relative' && enrollmentDate) {
            const dueDate = new Date(enrollmentDate * 1000);
            dueDate.setDate(dueDate.getDate() + deadline.daysAfterEnrollment);
            return now.getTime() > dueDate.getTime();
        }
        if (deadline.type === 'fixed') {
            return now.getTime() > deadline.timestamp * 1000;
        }
        return false;
    }, [deadline, enrollmentDate, now]);

    const isDueToday = useMemo((): boolean => {
        if (!deadline) return false;
        if (deadline.type === 'fixed') {
            const dueDate = new Date(deadline.timestamp * 1000);
            return (
                dueDate.getDate() === now.getDate() &&
                dueDate.getMonth() === now.getMonth() &&
                dueDate.getFullYear() === now.getFullYear() &&
                !isOverdue
            );
        }
        if (deadline.type === 'relative' && enrollmentDate) {
            const dueDate = new Date(enrollmentDate * 1000);
            dueDate.setDate(dueDate.getDate() + deadline.daysAfterEnrollment);
            return (
                dueDate.getDate() === now.getDate() &&
                dueDate.getMonth() === now.getMonth() &&
                dueDate.getFullYear() === now.getFullYear() &&
                !isOverdue
            );
        }
        return false;
    }, [deadline, enrollmentDate, isOverdue, now]);

    const overdueMessage = useMemo((): string => {
        if (!deadline) return '';
        if (deadline.type === 'fixed') {
            const dueDate = new Date(deadline.timestamp * 1000);
            const daysOverdue = Math.floor(
                (now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)
            );

            if (daysOverdue === 0) {
                return `The lesson was due today at ${dueDate.toLocaleTimeString(undefined, {
                    hour: '2-digit',
                    minute: '2-digit',
                })}`;
            }
            return `The lesson was due ${daysOverdue} ${daysOverdue === 1 ? 'day' : 'days'} ago`;
        }
        if (deadline.type === 'relative' && enrollmentDate) {
            const dueDate = new Date(enrollmentDate * 1000);
            dueDate.setDate(dueDate.getDate() + deadline.daysAfterEnrollment);
            const daysOverdue = Math.floor(
                (now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)
            );

            if (daysOverdue === 0) {
                return `The lesson was due today at ${dueDate.toLocaleTimeString(undefined, {
                    hour: '2-digit',
                    minute: '2-digit',
                })}`;
            }
            return `The lesson was due ${daysOverdue} ${daysOverdue === 1 ? 'day' : 'days'} ago`;
        }
        return '';
    }, [deadline, enrollmentDate, now]);

    if (!deadline) return null;

    const badgeColor = isOverdue ? 'red' : 'orange';

    switch (deadline.type) {
        case 'none':
            return null;
        case 'relative': {
            if (!enrollmentDate) return null;

            const dueDate = new Date(enrollmentDate * 1000);
            dueDate.setDate(dueDate.getDate() + deadline.daysAfterEnrollment);
            const daysRemaining = Math.ceil(
                (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
            );

            if (isOverdue) {
                return (
                    <Tooltip content={overdueMessage}>
                        <Badge
                            color={badgeColor}
                            whiteSpace="nowrap"
                            display="flex"
                            alignItems="center"
                            gap={5}
                            width="min-content"
                        >
                            <CalendarClockIcon
                                height={12}
                                width={12}
                                fill={`var(--${badgeColor}700)`}
                            />
                            Overdue
                        </Badge>
                    </Tooltip>
                );
            }

            return (
                <Tooltip
                    content={`This lesson must be completed by ${dueDate.toLocaleDateString(
                        undefined,
                        {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                        }
                    )} at ${dueDate.toLocaleTimeString(undefined, {
                        hour: '2-digit',
                        minute: '2-digit',
                    })}`}
                >
                    <Badge
                        color={badgeColor}
                        whiteSpace="nowrap"
                        display="flex"
                        alignItems="center"
                        gap={5}
                        width="min-content"
                    >
                        <CalendarClockIcon
                            height={12}
                            width={12}
                            fill={`var(--${badgeColor}700)`}
                        />
                        {isDueToday
                            ? 'Due today'
                            : `Due in ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}`}
                    </Badge>
                </Tooltip>
            );
        }
        case 'fixed': {
            const dueDate = new Date(deadline.timestamp * 1000);
            const date = dueDate.toLocaleDateString(undefined, {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
            });
            const time = dueDate.toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit',
            });
            return (
                <Tooltip
                    content={
                        isOverdue
                            ? overdueMessage
                            : `This lesson must be completed by ${date} at ${time}`
                    }
                >
                    <Badge
                        color={badgeColor}
                        whiteSpace="nowrap"
                        display="flex"
                        alignItems="center"
                        gap={5}
                        width="min-content"
                    >
                        <CalendarClockIcon
                            height={12}
                            width={12}
                            fill={`var(--${badgeColor}700)`}
                        />
                        {isOverdue ? 'Overdue' : isDueToday ? 'Due today' : `Due by ${date}`}
                    </Badge>
                </Tooltip>
            );
        }
        default:
            return null;
    }
};
